import React, { useCallback, useEffect, useState } from "react";
import Store from "@apis/store";
import { Category } from "@apis/index";
import { toast } from "react-toastify";
import useConfig from "@hooks/useConfig";
import styled from "@emotion/styled";
import Button from "@components/button";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import { useModal } from "@context/ModalContext";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 500px; /* Fixed height for modal content */
`;

const ScrollableList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  /* Custom scrollbar styling if needed */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 4px;
  }
`;

const StoreCard = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px;
  margin-bottom: 10px;
  background: ${(props) => (props.selected ? "#f0f7ff" : "#ffffff")};
  border: 1px solid ${(props) => (props.selected ? "#0C70BC" : "#e0e0e0")};
  border-radius: 8px;
  transition: all 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    border-color: ${(props) => (props.selected ? "#0C70BC" : "#b0b0b0")};
  }
`;

const CheckboxContainer = styled.div`
  margin-right: 15px;
  display: flex;
  align-items: center;
`;

const SelectionBadge = styled.div<{ selected: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${(props) => (props.selected ? "#0C70BC" : "#fff")};
  border: 2px solid ${(props) => (props.selected ? "#0C70BC" : "#ccc")};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  margin-right: 15px;
  transition: all 0.2s;
`;

const Avatar = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  object-fit: cover;
  margin-right: 15px;
  border: 1px solid #f0f0f0;
`;

const Placeholder = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background-color: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  margin-right: 15px;
  border: 1px solid #e5e7eb;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Name = styled.h4`
  margin: 0 0 4px 0;
  font-size: 16px;
  color: #333;
  font-weight: 600;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 2px;
`;

const Detail = styled.span`
  font-size: 13px;
  color: #666;
`;

const Footer = styled.div`
  padding: 15px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  background: white;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 10px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #0c70bc;
  }
`;

const Loading = styled.div`
  text-align: center;
  padding: 20px;
  color: #888;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 30px;
  color: #999;
  font-style: italic;
`;

const StoreList = ({ type, categoryId, moduleId }) => {
  const { hideModal } = useModal();
  const { data: popularStores } = useSelector(
    (state: RootState) => state.popularStores,
  );
  const { data: featuredStores } = useSelector(
    (state: RootState) => state.featuredStores,
  );
  const fetchAllPopularStores = useCallback(async () => {
    await Category.popularStores({ state: "popularStores", id: categoryId });
  }, [categoryId]);
  const fetchAllFeaturedStores = useCallback(async () => {
    await Category.featuredStores({ state: "featuredStores", id: categoryId });
  }, [categoryId]);

  useEffect(() => {
    fetchAllPopularStores();
    fetchAllFeaturedStores();
  }, [categoryId, fetchAllPopularStores, fetchAllFeaturedStores, type]);

  useEffect(() => {
    if (type === "Popular") {
      const filtered =
        popularStores?.filter(
          (item: any) => item.categoryId === Number(categoryId),
        ) || [];
      const ids = filtered.map((item: any) => item.storeId);
      setSelectedStoreIds(ids);
    } else if (type === "Featured") {
      const filtered =
        featuredStores?.filter(
          (item: any) => item.categoryId === Number(categoryId),
        ) || [];
      const ids = filtered.map((item: any) => item.storeId);
      setSelectedStoreIds(ids);
    }
  }, [popularStores, featuredStores, type, categoryId]);

  const { bucketUrl } = useConfig();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  const fetchStores = async () => {
    try {
      setLoading(true);
      const res = await Store.list({
        params: {
          page,
          limit: 100,
          moduleId,
        },
      });
      if (res?.items) {
        setStores(res.items);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to fetch stores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStores();
  }, []);

  const handleSelect = (id) => {
    setSelectedStoreIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleSave = async () => {
    if (selectedStoreIds.length === 0) {
      toast.warning("Please select at least one store.");
      return;
    }

    try {
      const payload = {
        data: { storeIds: selectedStoreIds },
        id: categoryId,
      };

      if (type === "Popular") {
        await Category.addPopular(payload).then(() => {
          hideModal();
        });
      } else if (type === "Featured") {
        await Category.addFeatured(payload).then(() => {
          hideModal();
        });
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to save");
    }
  };

  const [search, setSearch] = useState("");

  const filteredStores = stores.filter((store: any) => {
    const name = store.storeName || store.name || "";
    return name.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <Container>
      <div style={{ padding: "0 10px" }}>
        <SearchInput
          placeholder="Search stores..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <ScrollableList>
        {loading && stores.length === 0 ? (
          <Loading>Loading stores...</Loading>
        ) : filteredStores.length > 0 ? (
          filteredStores.map((store: any) => {
            const selectionIndex = selectedStoreIds.indexOf(store.id);
            const isSelected = selectionIndex !== -1;
            return (
              <StoreCard
                key={store.id}
                selected={isSelected}
                onClick={() => handleSelect(store.id)}
              >
                <SelectionBadge selected={isSelected}>
                  {isSelected ? selectionIndex + 1 : ""}
                </SelectionBadge>
                {store.avatar ? (
                  <Avatar
                    src={`${bucketUrl}/${store.avatar}`}
                    alt={store.storeName || store.name}
                  />
                ) : (
                  <Placeholder>
                    {(store.storeName || store.name || "")
                      .split(" ")
                      .map((n: string) => n[0])
                      .join("")
                      .slice(0, 2)}
                  </Placeholder>
                )}
                <Info>
                  <Name>{store.storeName || store.name}</Name>
                  <MetaRow>
                    <Detail>
                      {store.storePhoneNo || store.phoneNo || "N/A"}
                    </Detail>
                    <Detail>•</Detail>
                    <Detail>{store.storeEmail || store.email || "N/A"}</Detail>
                  </MetaRow>
                  {store.module?.name && (
                    <Detail style={{ fontSize: "12px", color: "#888" }}>
                      Module: {store.module.name}
                    </Detail>
                  )}
                </Info>
              </StoreCard>
            );
          })
        ) : (
          <EmptyState>No stores found.</EmptyState>
        )}
      </ScrollableList>
      <Footer>
        <Button
          title="Save Selection"
          onClick={handleSave}
          disabled={selectedStoreIds.length === 0}
        />
      </Footer>
    </Container>
  );
};

export default StoreList;
